import { store } from "@store/index";
import {
  type SideNavigationV2Props,
  type PageV2Component,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import { close as closeSideNavigation } from "@store/features/layout/sideNavigationSlice";
import { toast } from "sonner";
import { trpc } from "@utils/api";
import {
  getAgents,
  updateEditorField,
  enterDraftMode,
  type AgentFormState,
  type ListAgent,
  type PersonaConfig,
} from "@store/features/agents/agentSlice";
import { SpanCol } from "skedyul-ui/ui";

/**
 * Type for stage from config
 */
type StageConfig = {
  id: string;
  type: "agent" | "tool" | "transform" | "output";
  resource?: string;
  prompt?: string;
  needs?: string[];
  inputs?: Record<string, string>;
  outputs?: Record<string, string>;
};

/**
 * Type for embedded agent from config
 */
type EmbeddedAgentConfig = {
  handle: string;
  name: string;
  description: string;
  system: string;
  llmModelId?: string;
  tools?: Array<string | { tool: string; name?: string }>;
};

/**
 * Get stage type icon name
 */
function getStageTypeIcon(type: StageConfig["type"]): string {
  switch (type) {
    case "agent":
      return "Bot";
    case "tool":
      return "Wrench";
    case "transform":
      return "Shuffle";
    case "output":
      return "ArrowRight";
    default:
      return "Circle";
  }
}

export const editAgentFlowSchema = (
  args: SideNavigationComponentProps<
    PageV2Component,
    {
      subdomain: string;
      llmModels?: Array<{ value: string; label: string; description?: string }>;
      draftDeploymentGroupId?: string | null;
    }
  >,
): SideNavigationV2Props<PageV2Component> => {
  const {
    agents: {
      flow: { scopedAgentId },
      agents: { data: agents },
      editor: { working, isSaving },
      draft: { isActive: isDraftActive, isSaving: isDraftSaving },
    },
    sideNavigation: {
      right: { isOpen },
    },
  } = args.state;

  const agent = agents.find((a) => a.id === scopedAgentId);
  const isNew = scopedAgentId === "new";

  // Get draft deployment group ID from params or state
  const draftDeploymentGroupId =
    args.routerContext.params.draftDeploymentGroupId;

  // Extract config from agent (now included via versionConfig)
  const agentWithConfig = agent as
    | (ListAgent & { versionConfig?: Record<string, unknown> })
    | undefined;
  const config = agentWithConfig?.versionConfig;

  // Check if this is a multi-stage agent
  const stages = (config?.stages as StageConfig[] | undefined) || [];
  const embeddedAgents =
    (config?.agents as EmbeddedAgentConfig[] | undefined) || [];
  const isMultiStage = stages.length > 0;

  const llmModels = args.routerContext.params.llmModels || [];

  const formState = working || {
    id: agent?.id || "",
    name: agent?.name || "",
    description: agent?.description || "",
    system: (agent as unknown as { system?: string })?.system || "",
    llmModelId: agent?.llmModelId || "",
    persona:
      (agent as unknown as { persona?: PersonaConfig | null })?.persona || null,
  };

  const handleFieldChange = (key: keyof AgentFormState, value: string) => {
    store.dispatch(updateEditorField({ key, value }));
  };

  const handlePersonaFieldChange = (field: "name" | "style", value: string) => {
    const currentPersona = formState.persona || {
      name: "",
      voice: { style: "" },
    };
    const updatedPersona: PersonaConfig = {
      ...currentPersona,
      name: field === "name" ? value : currentPersona.name,
      voice: {
        ...currentPersona.voice,
        style: field === "style" ? value : currentPersona.voice?.style || "",
      },
    };
    store.dispatch(
      updateEditorField({
        key: "persona",
        value: updatedPersona as unknown as string,
      }),
    );
  };

  return {
    side: "right",
    isOpen,
    width: "600px",
    component: {
      component: "PageV2",
      props: {
        title: formState.name || "New Agent",
        hideTitle: true,
        header: {
          icon: "Bot",
          primaryAction: {
            isLoading: isSaving || isDraftSaving,
            label: isNew
              ? "Create Agent"
              : isDraftActive
                ? "Update Draft"
                : "Save as Draft",
            handleClick: async () => {
              try {
                if (isNew) {
                  toast.info("Create agent functionality coming soon");
                  return;
                }

                if (!working || !agent) return;

                // Get agent handle
                const agentWithHandle = agent as ListAgent & {
                  handle?: string;
                };
                const handle =
                  agentWithHandle.handle ||
                  agent.name.toLowerCase().replace(/\s+/g, "-");

                // Get existing config or create minimal config
                const existingConfig = config || {};

                // Merge edited fields into config
                const mergedConfig = {
                  ...existingConfig,
                  handle,
                  name: working.name,
                  description: working.description,
                  system: working.system,
                  persona: working.persona || undefined,
                  llmModelId: working.llmModelId || undefined,
                };

                // Save as draft
                const result = await trpc.agents.saveDraft.mutate({
                  config: mergedConfig,
                  deploymentGroupId: draftDeploymentGroupId || undefined,
                });

                // Enter draft mode
                store.dispatch(
                  enterDraftMode({
                    config: mergedConfig,
                    deploymentGroupId: result.version.deploymentGroupId!,
                  }),
                );

                // Refresh agents list to show updated data
                store.dispatch(getAgents());
                toast.success("Draft saved");
              } catch (error) {
                console.error("Failed to save draft:", error);
                toast.error("Failed to save draft");
              }
            },
          },
        },
        content: {
          structure: "sidebar",
          components: [
            {
              type: "Card",
              title: "Details",
              props: {
                header: {
                  title: "Agent Details",
                  description: "Configure the basic information for this agent",
                },
                form: {
                  formVersion: "v1",
                  id: "agent-details-form",
                  fields: [
                    {
                      component: "Input",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        type: "text",
                        id: "agent-name",
                        label: "Name",
                        leftIcon: "Bot",
                        value: formState.name,
                        placeholder: "Enter agent name",
                        helpText: "The display name for this agent",
                        handleChange: (
                          e: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          handleFieldChange("name", e.target.value);
                        },
                      },
                    },
                    {
                      component: "Select",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "agent-model",
                        label: "LLM Model",
                        placeholder: "Select a model",
                        helpText: "The AI model powering this agent",
                        value: formState.llmModelId,
                        items: llmModels,
                        handleChange: (value: string) => {
                          handleFieldChange("llmModelId", value);
                        },
                      },
                    },
                    {
                      component: "Textarea",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "agent-description",
                        label: "Description",
                        value: formState.description,
                        placeholder: "Describe what this agent does...",
                        helpText: "A brief description of the agent's purpose",
                        rows: 3,
                        handleChange: (
                          e: React.ChangeEvent<HTMLTextAreaElement>,
                        ) => {
                          handleFieldChange("description", e.target.value);
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "System Prompt",
              props: {
                header: {
                  title: "System Prompt",
                  description: "Define the agent's behavior and instructions",
                },
                form: {
                  formVersion: "v1",
                  id: "agent-system-form",
                  fields: [
                    {
                      component: "Textarea",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "agent-system",
                        label: "System Prompt",
                        value: formState.system,
                        placeholder:
                          "Enter the system prompt that defines this agent's behavior...",
                        helpText:
                          "Instructions that guide the agent's responses",
                        rows: 10,
                        handleChange: (
                          e: React.ChangeEvent<HTMLTextAreaElement>,
                        ) => {
                          handleFieldChange("system", e.target.value);
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Persona",
              props: {
                header: {
                  title: "Persona",
                  description: "Optional persona for response generation",
                },
                form: {
                  formVersion: "v1",
                  id: "agent-persona-form",
                  fields: [
                    {
                      component: "Input",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        type: "text",
                        id: "agent-persona-name",
                        label: "Persona Name",
                        leftIcon: "User",
                        value: formState.persona?.name || "",
                        placeholder: "e.g., Brett",
                        helpText: "The name used when generating responses",
                        handleChange: (
                          e: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          handlePersonaFieldChange("name", e.target.value);
                        },
                      },
                    },
                    {
                      component: "Textarea",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "agent-persona",
                        label: "Persona Prompt",
                        value: formState.persona?.voice?.style || "",
                        placeholder:
                          "Define the persona's tone, style, and format rules...",
                        helpText:
                          "Enables two-phase response generation for high-quality outputs",
                        rows: 8,
                        handleChange: (
                          e: React.ChangeEvent<HTMLTextAreaElement>,
                        ) => {
                          handlePersonaFieldChange("style", e.target.value);
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Tools",
              hidden: !agent || isMultiStage,
              props: {
                header: {
                  title: "Tools Configuration",
                  description: "Tools are configured via YAML",
                },
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "Textarea" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "tools-info",
                        label: "Configuration",
                        value:
                          "Tools for this agent are defined in the agent's YAML configuration file. Deploy the agent with updated YAML to modify tools.",
                        isDisabled: true,
                        rows: 3,
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "List",
              title: "Stages",
              hidden: !agent || !isMultiStage,
              props: {
                items: stages.map((stage) => ({
                  id: stage.id,
                  title: stage.id,
                  description: stage.resource || `${stage.type} stage`,
                  leftIcon: getStageTypeIcon(stage.type),
                  badge: {
                    label: stage.type,
                    variant:
                      stage.type === "agent"
                        ? "default"
                        : stage.type === "tool"
                          ? "secondary"
                          : stage.type === "output"
                            ? "outline"
                            : "secondary",
                  },
                  metadata: stage.needs?.length
                    ? `depends on: ${stage.needs.join(", ")}`
                    : undefined,
                  handleClick: () => {
                    toast.info(`Edit stage: ${stage.id}`);
                  },
                })),
                emptyState: {
                  title: "No stages defined",
                  description: "Add stages to create a multi-stage pipeline",
                },
              },
            },
            {
              type: "List",
              title: "Embedded Agents",
              hidden: !agent || !isMultiStage || embeddedAgents.length === 0,
              props: {
                items: embeddedAgents.map((embeddedAgent) => ({
                  id: embeddedAgent.handle,
                  title: embeddedAgent.name,
                  description: embeddedAgent.description,
                  leftIcon: "Bot",
                  badge: {
                    label: embeddedAgent.handle,
                    variant: "outline" as const,
                  },
                  handleClick: () => {
                    toast.info(`Edit agent: ${embeddedAgent.handle}`);
                  },
                })),
              },
            },
          ],
        },
      },
    },
  };
};
